import EventEmitter from "base/EventEmitter";
import FormValidator from "base/FormValidator";
import GlobalEvents from "base/GlobalEvents";
import Message from "base/Message";
import PromiseHelper from "base/PromiseHelper";
import Logger from "logging/Logger";

/**
 * @property {FormValidator} validator
 */
export default class AjaxForm extends EventEmitter {

    constructor($element, requestFn, options = {}) {
        super();

        if ("function" !== typeof requestFn) {
            throw Error("Invalid request function passed to AjaxForm: " + requestFn);
        }

        this.$element = $element;
        this.requestFn = requestFn;
        this.options = $.extend(true, {}, DEFAULT_OPTIONS, options);

        this.createValidator();

        GlobalEvents.emit(EVENT_GLOBAL_AJAX_FORM_INITIALISED, this);
    }

    createValidator() {
        this.validator = FormValidator.attach(this.getFormElement(), this.options.validatorSettings);

        this.validator.on("submit", ($form) => this.submit($form));
    }

    destroy() {
        super.destroy();
        this.validator.destroy();
    }

    getButton() {
        return this.getFormElement().find(this.options.selectors.button);
    }

    getErrorContainer() {
        if ("function" === typeof this.options.selectors.errorContainer) {
            return this.options.selectors.errorContainer.apply(this);
        } else {
            return this.$element.find(this.options.selectors.errorContainer);
        }
    }

    getFormData() {
        return this.getFormElement().serializeJSON();
    }

    getFormElement() {
        return this.$element.is("form") ? this.$element : this.$element.find(this.options.selectors.form);
    }

    hideError() {
        if (!this.emit(EVENT_HIDE_ERROR)) return;

        this.getErrorContainer().empty();
    }

    isLoading() {
        return this.getButton().is("." + this.options.classes.loading);
    }

    reset() {
        this.stopLoading();
        this.hideError();
    }

    showError(e) {
        if (!this.emit(EVENT_SHOW_ERROR, e)) return;

        this.getErrorContainer().html(this.options.createError.call(this, e));
    }

    startLoading() {
        if (!this.emit(EVENT_START_LOADING)) return;

        this.getButton().addClass(this.options.classes.loading);
    }

    stopLoading() {
        if (!this.emit(EVENT_STOP_LOADING)) return;

        this.getButton().removeClass(this.options.classes.loading);
    }

    async submit($form = null) {
        if (!this.options.canSubmitWhileLoading && this.isLoading()) {
            return;
        }

        if ($form === null) {
            $form = this.getFormElement();
        }

        let data = this.getFormData();

        // Allow the submit to be cancelled
        if (!this.emit(EVENT_SUBMIT, {data})) return;

        this.startLoading();
        this.hideError();

        try {
            let result = await PromiseHelper.delayed_promise(this.requestFn.call(this, $form, data), this.options.submitDelay);

            if (!this.emit(EVENT_SUCCESS, result)) {
                return;
            }

            this.validator.reset();
        } catch (e) {
            Logger.error("Error from AjaxForm", e);
            this.validator.handleValidationException(e);
            if (this.emit(EVENT_ERROR, e)) {
                this.showError(e);
            }
        }

        this.stopLoading();
    }
}

AjaxForm.DEFAULT_SETTINGS = {
    classes: {
        loading: "btn--loading",
        errorMessage: "message message--error",
    },
    selectors: {
        form: "form",
        errorContainer: ".js-message-holder",
        button: ":submit",
    },
    validatorSettings: {inputOptions: {operator: "your"}},
    createError: function (e) {
        return (
            eotwJSXFactory('div', {className: this.options.classes.errorMessage}, [
                eotwJSXFactory('p', null, [e ? e.message : "An unknown error occurred"])
            ])
        );
    },
    submitDelay: 300,
    canSubmitWhileLoading: false,
};


AjaxForm.ADMIN_DEFAULT_OPTIONS = {
    classes: {
        loading: "admin-btn--loading",
    },
    validatorSettings: {inputOptions: {operator: "your"}},
    createError: function (e) {
        return Message.get_inline_message(e.message, 'An error occurred');
    },
};

export const DEFAULT_OPTIONS = AjaxForm.DEFAULT_SETTINGS;

export const EVENT_START_LOADING = "startLoading";
export const EVENT_STOP_LOADING = "stopLoading";

export const EVENT_SHOW_ERROR = "showError";
export const EVENT_HIDE_ERROR = "hideError";

export const EVENT_SUBMIT = "submit";
export const EVENT_SUCCESS = "success";
export const EVENT_ERROR = "error";

export const EVENT_GLOBAL_AJAX_FORM_INITIALISED = 'ajaxForm.initialised';
